import { http } from "@/shared/api/http";

export type PartnerStatus = "pending" | "active" | "suspended" | "terminated";

export interface GrowthPartner {
  _id: string;
  referral_code: string;
  status: PartnerStatus;
  created_at: string;
}

export interface GrowthActivity {
  _id: string;
  event_type: "commission" | "reversal";
  level: number;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  skip_reason?: string | null;
}

export interface GrowthDashboard {
  enrolled: boolean;
  partner: GrowthPartner | null;
  network: { direct_customers: number; second_level_customers: number };
  balances: { pending: number; matured: number; paid: number; reversed: number };
  recent_activity: GrowthActivity[];
  payout: null | {
    eligible: boolean; reasons: string[]; available_cents: number; minimum_cents: number;
    cooldown_hours: number; cooldown_until?: string | null; open_request?: { _id: string; status: string; amount_cents: number } | null;
  };
  policy: {
    version: string; l1_rate_bps: number; l2_rate_bps: number;
    l2_window_months: number; hold_days: number; minimum_payout_cents: number;
    payout_cooldown_hours: number; levels: number;
  };
}

export interface GrowthIntegritySignal {
  code: string;
  severity: "review" | "high";
  detail: string;
  evidence: Record<string, unknown>;
}

export interface GrowthIntegrityResult {
  partner_id: string;
  risk_level: "clear" | "review" | "high";
  signals: GrowthIntegritySignal[];
  automatic_action_taken: false;
  founder_review_required: boolean;
}

export interface GrowthPayoutRequest {
  _id: string;
  partner_id: string;
  status: string;
  amount_cents: number;
  requested_at: string;
  money_moved?: boolean;
}

export interface GrowthFounderQueue {
  pending_partners: GrowthPartner[];
  requested_payouts: GrowthPayoutRequest[];
  flagged_partners: Array<{ partner: GrowthPartner; integrity: GrowthIntegrityResult }>;
  counts: { pending_partners: number; requested_payouts: number; flagged_partners: number };
  read_only: true;
  generated_at: string;
}

const base = (ws: string) => `/workspaces/${ws}/growth-network`;
export const GROWTH_REFERRAL_STORAGE_KEY = "orkexra.growth.referral.v1";

export const growthSignupLink = (code: string): string =>
  `${window.location.origin}/join/${encodeURIComponent(code)}`;
export const rememberGrowthReferral = (code: string): void => {
  try { window.sessionStorage.setItem(GROWTH_REFERRAL_STORAGE_KEY, code); } catch { /* unavailable */ }
};
export const readGrowthReferral = (): string | null => {
  try { return window.sessionStorage.getItem(GROWTH_REFERRAL_STORAGE_KEY); } catch { return null; }
};
export const clearGrowthReferral = (): void => {
  try { window.sessionStorage.removeItem(GROWTH_REFERRAL_STORAGE_KEY); } catch { /* unavailable */ }
};
export const validateGrowthReferral = (code: string): Promise<{ valid: true; referral_code: string }> =>
  http(`/growth-network/referrals/${encodeURIComponent(code)}`);
export const getGrowthDashboard = (ws: string): Promise<GrowthDashboard> => http(`${base(ws)}/dashboard`);
export const enrollGrowthPartner = (ws: string): Promise<GrowthPartner> =>
  http(`${base(ws)}/partner/enroll`, { method: "POST" });
export const requestGrowthPayout = (ws: string): Promise<{ _id: string; status: string; amount_cents: number }> =>
  http(`${base(ws)}/partner/payouts/request`, { method: "POST" });
export const createGrowthAttribution = (ws: string, referralCode: string): Promise<{ _id: string }> =>
  http(`${base(ws)}/attribution`, { method: "POST", body: { referral_code: referralCode, source: "direct_signup_link" } });
export const getGrowthFounderQueue = (ws: string): Promise<GrowthFounderQueue> =>
  http(`${base(ws)}/admin/review-queue`);
export const setGrowthPartnerStatus = (ws: string, partnerId: string, status: PartnerStatus): Promise<GrowthPartner> =>
  http(`${base(ws)}/partners/${partnerId}/status`, { method: "PATCH", body: { status } });
export const decideGrowthPayout = (
  ws: string, requestId: string, decision: "approved" | "rejected", reason?: string,
): Promise<GrowthPayoutRequest> => http(`${base(ws)}/admin/payouts/${requestId}/decision`, {
  method: "PATCH", body: { decision, reason: reason || null },
});
